import { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  TextField,
  Box,
  Typography,
  Divider,
  CircularProgress,
  Chip,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Save as SaveIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/rpc';
import { queryKeys } from '../../api/queryKeys';
import { useUIStore } from '../../stores/uiStore';
import { useAuth } from '../auth';
import type { SavedSearch, WhereBean } from '../../api/types';

interface SavedSearchDialogProps {
  open: boolean;
  onClose: () => void;
  /** When a search is selected, apply its criteria */
  onApplySearch?: (criterion: WhereBean[], searchName: string) => void;
}

/**
 * Dialog for managing saved searches — list, load, save, delete.
 * Mirrors GWT's SavedSearchesDialog / SavedSearchesPanel.
 */
export const SavedSearchDialog = ({ open, onClose, onApplySearch }: SavedSearchDialogProps) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const advancedFilters = useUIStore((s) => s.advancedFilters);
  const setAdvancedFilters = useUIStore((s) => s.setAdvancedFilters);

  const [saveName, setSaveName] = useState('');
  const [saveAsDefault, setSaveAsDefault] = useState(false);
  const [saveOpenInNewTab, setSaveOpenInNewTab] = useState(true);
  const [showSaveForm, setShowSaveForm] = useState(false);

  const { data: searches, isLoading } = useQuery({
    queryKey: queryKeys.savedSearches,
    queryFn: () => api.getSavedSearches(user?.objKey),
    enabled: open,
  });

  const deleteMutation = useMutation({
    mutationFn: (searchId: string) => api.deleteSavedSearch(searchId, user?.objKey),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.savedSearches });
    },
  });

  const saveMutation = useMutation({
    mutationFn: (params: { searchName: string; criterion: WhereBean[]; isDefault: boolean; openInANewTab: boolean }) =>
      api.editSavedSearch(
        {
          searchName: params.searchName,
          criterion: params.criterion,
          isDefault: params.isDefault,
          openInANewTab: params.openInANewTab,
          parentKey: user?.objKey,
        },
        true,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.savedSearches });
      setShowSaveForm(false);
      setSaveName('');
    },
  });

  const handleLoadSearch = useCallback(
    (search: SavedSearch) => {
      if (search.criterion && search.criterion.length > 0) {
        setAdvancedFilters(search.criterion);
        onApplySearch?.(search.criterion, search.searchName);
      }
      onClose();
    },
    [setAdvancedFilters, onApplySearch, onClose],
  );

  const handleDelete = useCallback(
    (searchId: string) => {
      deleteMutation.mutate(searchId);
    },
    [deleteMutation],
  );

  const handleSaveCurrent = useCallback(() => {
    if (!saveName.trim()) return;
    saveMutation.mutate({
      searchName: saveName.trim(),
      criterion: advancedFilters,
      isDefault: saveAsDefault,
      openInANewTab: saveOpenInNewTab,
    });
  }, [saveName, advancedFilters, saveAsDefault, saveOpenInNewTab, saveMutation]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Saved Searches</DialogTitle>
      <DialogContent sx={{ minHeight: 300 }}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={32} />
          </Box>
        ) : searches && searches.length > 0 ? (
          <List dense>
            {searches.map((search) => (
              <ListItem
                key={search.id}
                component="div"
                sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                onClick={() => handleLoadSearch(search)}
              >
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <SearchIcon sx={{ fontSize: 16 }} />
                      <Typography variant="body2">{search.searchName}</Typography>
                      {search.isDefault && (
                        <Chip label="Default" size="small" color="primary" sx={{ height: 18, fontSize: 10 }} />
                      )}
                      {search.isPredefined && (
                        <Chip label="Predefined" size="small" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
                      )}
                    </Box>
                  }
                  secondary={
                    search.criterion
                      ? `${search.criterion.length} filter${search.criterion.length !== 1 ? 's' : ''}`
                      : 'No filters'
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(search.id);
                    }}
                    disabled={search.isPredefined}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
            No saved searches found.
          </Typography>
        )}

        <Divider sx={{ my: 2 }} />

        {showSaveForm ? (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Save Current Search ({advancedFilters.length} filter{advancedFilters.length !== 1 ? 's' : ''})
            </Typography>
            <TextField
              autoFocus
              fullWidth
              size="small"
              label="Search Name"
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              sx={{ mb: 1 }}
            />
            <FormControlLabel
              control={<Checkbox size="small" checked={saveAsDefault} onChange={(e) => setSaveAsDefault(e.target.checked)} />}
              label="Set as default search"
            />
            <FormControlLabel
              control={<Checkbox size="small" checked={saveOpenInNewTab} onChange={(e) => setSaveOpenInNewTab(e.target.checked)} />}
              label="Open in new tab"
            />
            <Box display="flex" gap={1} mt={1}>
              <Button
                variant="contained"
                size="small"
                startIcon={<SaveIcon />}
                onClick={handleSaveCurrent}
                disabled={!saveName.trim() || advancedFilters.length === 0 || saveMutation.isPending}
              >
                Save
              </Button>
              <Button size="small" onClick={() => setShowSaveForm(false)}>
                Cancel
              </Button>
            </Box>
          </Box>
        ) : (
          <Button
            variant="outlined"
            size="small"
            startIcon={<SaveIcon />}
            onClick={() => setShowSaveForm(true)}
            disabled={advancedFilters.length === 0}
          >
            Save Current Search
          </Button>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
